import {Inject, Injectable, Injector} from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import {ApiService, BrandingModeService, LoggerService, URLS} from '@netfoundry-ui/shared/services';
import LogRocket from 'logrocket';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import {Environment, ENVIRONMENT, Organization} from "@netfoundry-ui/shared/model";
import { IamService } from './iam.service';
import { LOGIN_SERVICE, LoginServiceType } from './login-service.type';

@Injectable({
    providedIn: 'root',
})
export class Auth0Service {
    isAuthenticated$: Observable<boolean>;
    idToken: any;
    user: any;
    accessToken: any;
    expiration: any;
    appState: any;
    jwtHelper: JwtHelperService;
    authenticated: any;

    constructor(
        private logger: LoggerService,
        private auth0Angular: AuthService,
        private iamService: IamService,
        private brandingService: BrandingModeService,
        private apiService: ApiService,
        private router: Router,
        private injector: Injector,
        @Inject(ENVIRONMENT) public environment: Environment,
    ) {
        auth0Angular.error$?.subscribe((error) => this.handleError(error));
        this.jwtHelper = new JwtHelperService();

        auth0Angular.idTokenClaims$?.subscribe((claims) => {
            if (claims?.exp) {
                const expiresAt = JSON.stringify(claims.exp * 1000);
                localStorage.setItem('expires_at', expiresAt);
            }
            this.idToken = claims?.__raw;
            localStorage.setItem('profile_email', claims?.email);
            localStorage.setItem('profile_nick', claims?.nickname);
            localStorage.setItem('profile_picture', claims?.picture);
        });
        auth0Angular.appState$?.subscribe((state) => {
            let nfState: any;
            try {
                nfState = this.jwtHelper.decodeToken(state.nfToken);
            } catch (err) {
                // A malformed/missing nfToken would otherwise throw uncaught out of this
                // subscription, silently aborting the flow. Surface it instead (MOP-27516).
                this.logger.error('Failed to decode nfToken from appState', err);
                if (state.inviteKey) {
                    this.handleInvitationFailure('malformed nfToken in appState', state.inviteKey, err);
                }
                return;
            }
            if (state.inviteKey) {
                // Invitation flow: _setSession must wait until after confirmInvite so that
                // getMyOrg() succeeds (the identity mapping is created by confirmInvite).
                const inviteKey = state.inviteKey;
                const target = this.environment['isCloudZiti'] ? '/dashboard' : '/v7/dashboard';
                this.iamService.acceptInvite(inviteKey).subscribe({
                    next: (res: any) => {
                        const inviteToken = res?.nfToken;
                        if (inviteToken) {
                            this.iamService.confirmInvite(inviteKey, inviteToken).subscribe({
                                next: () => {
                                    this.logger.info('Invitation accepted');
                                    if (nfState?.tenantLabel) {
                                        // Navigate only once the session is established. If getMyOrg
                                        // fails, _setSession emits false — treat it as a failed
                                        // acceptance attempt rather than navigating into the console
                                        // with no org context (MOP-27516).
                                        this._setSession(nfState.tenantLabel)
                                            .pipe(take(1))
                                            .subscribe((sessionReady) => {
                                                if (sessionReady) {
                                                    this.router.navigateByUrl(target);
                                                } else {
                                                    this.handleInvitationFailure(
                                                        'session setup failed after confirmInvite (getMyOrg)',
                                                        inviteKey
                                                    );
                                                }
                                            });
                                    } else {
                                        this.router.navigateByUrl(target);
                                    }
                                },
                                error: (err) =>
                                    this.handleInvitationFailure(
                                        'confirmInvite was rejected by the platform',
                                        inviteKey,
                                        err
                                    ),
                            });
                        } else {
                            this.handleInvitationFailure('accept-initiate returned no nfToken', inviteKey, res);
                        }
                    },
                    error: (err) => this.handleInvitationFailure('accept-initiate failed post-login', inviteKey, err),
                });
            } else {
                // Normal login flow
                if (nfState?.tenantLabel) {
                    this._setSession(nfState.tenantLabel);
                }
            }
            this.appState = state;
        });
        auth0Angular.user$?.subscribe(async (user) => {
            if (user) {
                const u = await firstValueFrom(auth0Angular.idTokenClaims$.pipe(take(1)));
                this.user = u;
                LogRocket.identify(user.email, {
                    name: user.name,
                    email: user.email,
                });
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const _hsq = (window._hsq = window._hsq || []);
                _hsq.push([
                    'identify',
                    {
                        email: user.email,
                    },
                ]);
            }
        });
        this.isAuthenticated$ = auth0Angular.isAuthenticated$;
        this.isAuthenticated$?.subscribe(async (result) => {
            this.authenticated = result;
            const enjoyhint_instance = window['enjoyhint_instance'];
            if (!this.authenticated && enjoyhint_instance?.trigger) {
                enjoyhint_instance.trigger('skip');
            }
        });
    }

    loginWithRedirect(state?: any): void {
        // Call this to redirect the user to the login page
        this.auth0Angular.loginWithRedirect(state);
    }

    logout(options: any = { logoutParams: { returnTo: window.location.origin } }): void {
        this.auth0Angular.logout(options);
    }

    /**
     * This invitation-acceptance attempt failed post-login (acceptInvite/confirmInvite rejected, no
     * nfToken returned, a malformed token, or session setup not completing). The invitation itself is
     * still valid — the usual cause is signing in with a different account than the one the invitation
     * was sent to. Tear down the session (so a wrong/previous user is not left in the console — the
     * AuthenticationGuard only checks isAuthenticated$, and clearStorage alone leaves the Auth0 SDK
     * cache intact) and return to the invitation review screen, flagged so the user is told the
     * attempt didn't match and can retry (MOP-27516).
     *
     * LoginService is resolved lazily via its injection token to avoid a circular dependency
     * (LoginService injects Auth0Service). A local-only logout keeps this a client-side navigation.
     */
    private handleInvitationFailure(detail: string, inviteKey: string, err?: any): void {
        this.logger.error(`Invitation acceptance failed: ${detail}`, err);
        const loginService = this.injector.get(LOGIN_SERVICE) as LoginServiceType;
        loginService.logout(
            false,
            true,
            { queryParams: { invitationKey: inviteKey, acceptError: 'true' } },
            true,
            URLS.INVITATION
        );
    }

    getAccessTokenSilently(options?: any): Observable<any> {
        return this.auth0Angular.getAccessTokenSilently(options);
    }

    handleError(error: Error): void {
        // Swallow silent-refresh "Missing Refresh Token" — LoginService handles
        // the authenticated case; pre-login it's noise.
        const message = (error as any)?.message || '';
        const code = (error as any)?.error || '';
        if (code === 'missing_refresh_token' || /Missing Refresh Token/i.test(message)) {
            return;
        }
        this.logger.error(error.message);
    }

    getErrorObservable(): Observable<any> {
        return this.auth0Angular.error$;
    }

    getUserObservable(): Observable<any> {
        return this.auth0Angular.user$;
    }

    getUser() {
        return this.auth0Angular.user$.pipe(
            take(1),
            tap((user) => {
                this.logger.info(`returning ${JSON.stringify(user)}`);
            })
        );
    }

    /**
     * Establishes the NF session for the authenticated user. Emits `true` once org data is loaded
     * and callers may proceed (e.g. navigate to the dashboard), or `false` if getMyOrg fails so the
     * caller does not navigate into the console with no org context (MOP-27516). The invitation flow
     * treats `false` as a failed acceptance attempt; the normal-login path ignores the return value.
     *
     * Returns a ReplaySubject so a caller subscribing after getMyOrg resolves still receives
     * the result; the getMyOrg call fires eagerly regardless of subscription.
     */
    public _setSession(tenantLabel): Observable<boolean> {
        // Set the time that the access token will expire at
        this.logger.info('Setting session for tenant: ' + tenantLabel);
        //const jwt = this.jwtHelper.decodeToken(authResult.accessToken);
        // fetch and store the Tenant info
        const authToken = this.getAuthToken();
        const jwt = this.jwtHelper.decodeToken(authToken);
        localStorage.setItem('Auth0Id', jwt.sub);
        localStorage.setItem('connectionId', jwt['https://netfoundry.io/auth0/connectionId']);
        localStorage.setItem('tenantLabel', tenantLabel);
        const sessionReady$ = new ReplaySubject<boolean>(1);
        this.iamService.getMyOrg().subscribe({
            next: (result: any) => {
            this.logger.info('Tenant Info', result);
            localStorage.setItem('tenantName', result.name);
            localStorage.setItem('tenantLabel', result.label);
            localStorage.setItem('tenantId', result.id);
            localStorage.setItem('loggedInTenantId', result.id);

            // set branding once we have the tenant label
            const html = document.getElementsByTagName('body')[0];

            if (!this.environment.realmsSupported) {
              this.brandingService.fetchBrandCss().then((rawCss) => {
                html.style.cssText = rawCss;
              });
            }
            // checking if the tenant the user is logged into is the same as the last tenant that was logged into
            // in this session
            if (result.id !== sessionStorage.getItem('lastTenantId')) {
                // if the tenants are different, remove the old growler messages
                sessionStorage.removeItem('growlerErrors');
            }
            sessionStorage.setItem('lastTenantId', result.id);
            this.apiService.setCurrentOrganization(new Organization(result));
            sessionReady$.next(true);
            sessionReady$.complete();
            },
            error: (err: any) => {
                // Emit false so the caller does not navigate into the console without org context.
                // The invitation flow handles this as a failed acceptance attempt; normal login
                // ignores the result.
                this.logger.error('getMyOrg failed during session setup', err);
                sessionReady$.next(false);
                sessionReady$.complete();
            },
        });
        return sessionReady$.asObservable();
    }

    getAuthToken() {
        const setlocalStorage = Object.entries(localStorage);
        const bearerJwtObjectArray = setlocalStorage.find(([key]) => key.startsWith('@@auth0spajs@@') &&!key.includes('@@user@@'));
        const bearerJwtObject = JSON.parse(bearerJwtObjectArray[1]);
        const bearerJwt = bearerJwtObject.body.access_token;
        return bearerJwt;
    }
}
